import { toRaw } from 'vue';
import { applyKioskLanguages, currentLocale } from '@/app/i18n';
import { fetchShared, queryClient } from '@/app/query-client';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import type { ApiQuery } from '@/services/api/client';
import {
  type ConfigQueryOptions,
  kioskQuery,
  partnerQuery,
  styleQuery,
} from '@/services/api/endpoints/kiosk';
import { kioskDesign } from '@/services/KioskDesign';
import { kioskStyle } from '@/services/KioskStyle';
import { payment } from '@/services/Payment';
import { printing } from '@/services/Printing';
import { session } from '@/services/Session';
import { sentryService } from '@/services/telemetry/sentry';
import type {
  KioskResponse,
  PartnerResponse,
  StyleResponse,
} from '@/shared/types/api';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import preloadImage from '@/shared/utils/preload-image';

interface KioskBranding {
  kiosk: KioskResponse;
  partner: PartnerResponse | null;
}

export interface KioskConfigSnapshot extends KioskBranding {
  styles: StyleResponse[];
}

interface ReadMode extends ConfigQueryOptions {
  fetch: <T>(query: ApiQuery<T>) => Promise<T>;
}

// The boot takes an answer that the cache has. A refresh asks the backend
// each time, and it is quiet because the kiosk keeps its config on a failure.
const BOOT: ReadMode = {
  quiet: false,
  fetch: <T>(query: ApiQuery<T>): Promise<T> => queryClient.fetchQuery(query),
};
const REFRESH: ReadMode = { quiet: true, fetch: fetchShared };

export const KIOSK_CONFIG_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
// A refresh reads the kiosk, the partner and each style. A return to Home asks
// again only when the kiosk got its config this long ago or more.
export const KIOSK_CONFIG_HOME_REFRESH_MIN_AGE_MS = 60 * 1000;
const STYLE_PREVIEW_PRELOAD_TIMEOUT_MS = 15_000;

function sameItems<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

function isManualAbort(error: unknown): boolean {
  return Boolean(
    (error as { context?: { isRequestManuallyAborted?: boolean } })?.context
      ?.isRequestManuallyAborted
  );
}

// A failed or hung preview must not hold back the styles.
async function preloadStylePreviews(styles: StyleResponse[]): Promise<void> {
  const previews = styles
    .filter((style) => style.preview_url)
    .map((style) =>
      preloadImage(style.preview_url as string).catch(() => null)
    );
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    Promise.all(previews),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, STYLE_PREVIEW_PRELOAD_TIMEOUT_MS);
    }),
  ]);
  clearTimeout(timer);
}

// A newer config applies only on Home without a session, so a session keeps
// its price, styles and printer; kiosk, partner and styles change together.
export class KioskConfig {
  private slug: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private brandingRequests = 0;
  private commits = 0;
  private refused: KioskConfigSnapshot | null = null;
  // The last time that the kiosk had the config of the backend. A fetch that a
  // session interrupted does not count: its config still waits for Home.
  private syncedAt = 0;
  // STEP_CHANGED fires in the router guard, before the route changes: the
  // refresh checks Home again when it commits.
  private readonly onStepChanged = ({ to }: { to: string | null }): void => {
    if (to === '' && !this.isConfigRecent()) {
      void this.refresh();
    }
  };

  constructor() {
    exposeToGlobalScope(this, 'KioskConfig');
  }

  // The boot is the first commit, but it applies each part as soon as its read
  // answers: the start screen waits for the kiosk and its partner only.
  async load(slug: string): Promise<KioskBranding> {
    this.slug = slug;
    const branding = await this.loadBranding(slug);
    this.syncedAt = Date.now();
    void this.loadStyles(branding.kiosk);
    return branding;
  }

  async loadBranding(slug: string): Promise<KioskBranding> {
    // A customer page that remounts for another session loads again; the
    // answer of the page it left must not paint over it.
    const request = ++this.brandingRequests;
    const isLatest = (): boolean => request === this.brandingRequests;
    let kiosk: KioskResponse;
    try {
      kiosk = await this.readKiosk(slug, BOOT);
    } catch (error) {
      useUiStore().showError('kiosk_not_found', error);
      throw error;
    }
    if (isLatest()) {
      kioskDesign.applyKiosk(kiosk);
    }
    const partner = await this.readPartner(kiosk, BOOT);
    if (isLatest()) {
      kioskDesign.partner = partner;
    }
    return { kiosk, partner };
  }

  private async loadStyles(kiosk: KioskResponse): Promise<void> {
    const commits = this.commits;
    kioskStyle.prepare(kiosk.portrait_styles_enable_selector ?? null);
    let styles: StyleResponse[];
    try {
      styles = await this.readStyles(kiosk, BOOT);
    } catch (error) {
      useUiStore().showError('art_style_fetch_error', error);
      return;
    }
    await preloadStylePreviews(styles);
    // A refresh that committed meanwhile brought newer styles.
    if (this.commits === commits) {
      this.applyStyles(kiosk, styles);
    }
  }

  private readKiosk(slug: string, mode: ReadMode): Promise<KioskResponse> {
    return mode.fetch(kioskQuery(slug, mode));
  }

  // A partner that does not answer: the boot goes on without one, and a refresh
  // keeps the partner that the kiosk has (none after such a boot). A new
  // partner must answer a refresh, because the payment can go to it.
  private async readPartner(
    kiosk: KioskResponse,
    mode: ReadMode
  ): Promise<PartnerResponse | null> {
    if (!kiosk.partner) {
      return null;
    }
    const samePartner = toRaw(kioskDesign.kiosk)?.partner === kiosk.partner;
    const known = toRaw(kioskDesign.partner) as PartnerResponse | null;
    try {
      return await mode.fetch(partnerQuery(kiosk.partner, mode));
    } catch (error) {
      if (mode === BOOT) {
        return null;
      }
      if (samePartner) {
        return known;
      }
      throw error;
    }
  }

  private readStyles(
    kiosk: KioskResponse,
    mode: ReadMode
  ): Promise<StyleResponse[]> {
    return Promise.all(
      Array.from(new Set(kiosk.portrait_style_ids ?? []), (id) =>
        mode.fetch(styleQuery(id, mode))
      )
    );
  }

  // Each apply counts: a read of styles that started before it brings older
  // styles.
  private applyStyles(kiosk: KioskResponse, styles: StyleResponse[]): void {
    this.commits++;
    kioskStyle.setStyles(styles, kiosk.portrait_styles_enable_selector);
  }

  private isFree(): boolean {
    return !(
      store.sessionId ||
      store.isResettingSession ||
      session.isCreating ||
      payment.isPaymentInProgress ||
      printing.isPrintingInProgress
    );
  }

  isIdle(): boolean {
    return router.step === '' && this.isFree();
  }

  // A system clock that went back also asks again.
  private isConfigRecent(): boolean {
    const age = Date.now() - this.syncedAt;
    return age >= 0 && age < KIOSK_CONFIG_HOME_REFRESH_MIN_AGE_MS;
  }

  refresh(): Promise<boolean> {
    if (!this.slug) {
      return Promise.resolve(false);
    }
    this.refreshing ??= this.fetchAndCommit(this.slug).finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async fetchAndCommit(slug: string): Promise<boolean> {
    await session.whenEnded();
    // An app that unmounted meanwhile (HMR, a test) fetches nothing more.
    if (!(this.refreshTimer && this.isFree())) {
      return false;
    }
    const current = toRaw(kioskDesign.kiosk);
    let next: KioskConfigSnapshot;
    try {
      next = await this.fetchFresh(slug);
    } catch (error) {
      if (!isManualAbort(error)) {
        sentryService.addBreadcrumb(
          'kiosk.config',
          'Kiosk config refresh failed, the kiosk keeps its config',
          { slug, error: (error as Error)?.message },
          'warning'
        );
      }
      return false;
    }
    if (!this.differs(next)) {
      this.syncedAt = Date.now();
      return false;
    }
    if (payment.isPartnerAccountMissing(next.kiosk, next.partner)) {
      // Every tick fetches the refused config again: one report per config.
      if (
        this.refused?.kiosk !== next.kiosk ||
        this.refused.partner !== next.partner
      ) {
        this.refused = next;
        sentryService.captureMessage(
          'Kiosk config refresh refused: partner payment without a Stripe Connect account',
          {
            level: 'error',
            tags: { payment_error: 'config_invalid', kiosk_slug: slug },
          }
        );
      }
      return false;
    }
    await preloadStylePreviews(next.styles);
    // The app may have unmounted, or the operator panel changed the kiosk,
    // while the fetch and the preload ran.
    if (
      !(this.refreshTimer && this.isIdle()) ||
      toRaw(kioskDesign.kiosk) !== current
    ) {
      return false;
    }
    await this.commit(next);
    this.syncedAt = Date.now();
    return true;
  }

  private async fetchFresh(slug: string): Promise<KioskConfigSnapshot> {
    const kiosk = await this.readKiosk(slug, REFRESH);
    const [partner, styles] = await Promise.all([
      this.readPartner(kiosk, REFRESH),
      this.readStyles(kiosk, REFRESH),
    ]);
    return { kiosk, partner, styles };
  }

  private differs(next: KioskConfigSnapshot): boolean {
    return (
      toRaw(kioskDesign.kiosk) !== next.kiosk ||
      toRaw(kioskDesign.partner) !== next.partner ||
      !sameItems(toRaw(kioskStyle.styles), next.styles)
    );
  }

  private async commit(next: KioskConfigSnapshot): Promise<void> {
    const previousLanguages = kioskDesign.kiosk?.languages ?? [];
    kioskDesign.applyKiosk(next.kiosk);
    kioskDesign.partner = next.partner;
    this.applyStyles(next.kiosk, next.styles);
    // The boot does its own printer, payment and language steps in
    // useKioskBootstrap, once the kiosk and its partner answer.
    printing.trackPrinterContext();
    payment.warnIfReaderMissing();
    sentryService.addBreadcrumb('kiosk.config', 'Kiosk config refreshed', {
      slug: next.kiosk.slug,
      styles_count: next.styles.length,
    });

    if (!sameItems(previousLanguages, next.kiosk.languages ?? [])) {
      applyKioskLanguages(next.kiosk.languages);
      const locale = currentLocale.value;
      if (locale && locale.code !== router.localeCode) {
        await router.redirectToLocale(locale.code, { silent: true });
      }
    }
  }

  // An operator change applies at once, also in a session.
  updateKiosk(change: (kiosk: KioskResponse) => KioskResponse): void {
    const current = toRaw(kioskDesign.kiosk) as KioskResponse | null;
    const slug = this.slug ?? current?.slug;
    if (!(current && slug)) {
      return;
    }
    const next = change(current);
    kioskDesign.applyKiosk(
      queryClient.setQueryData<KioskResponse>(
        kioskQuery(slug).queryKey,
        next
      ) ?? next
    );
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    router.on(router.EVENTS.STEP_CHANGED, this.onStepChanged);
    this.refreshTimer = setInterval(() => {
      if (this.isIdle()) {
        void this.refresh();
      }
    }, KIOSK_CONFIG_REFRESH_INTERVAL_MS);
  }

  stopAutoRefresh(): void {
    router.off(router.EVENTS.STEP_CHANGED, this.onStepChanged);
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

export const kioskConfig = new KioskConfig();
